// DataTrace Digital Gateway API Service
// Using property_detail product type
// Calls go through local proxy server to avoid CORS issues

// Pre-configured credentials (secret key requires last 3 characters to be entered by user)
// Client ID: 19b17037-e682-45d7-aa46-c47081fa6c3c
// Tenant ID: 4cc65fd6-9c76-4871-a542-eb12a5a7800c
// Key Expiration: 11/25/2026
let credentials = {
  clientId: '19b17037-e682-45d7-aa46-c47081fa6c3c',
  clientSecret: 'bHR8Q~yJ6SQsxQd2W4AsKQbCuqvv.d27be9Y5bzt', // Full secret pre-configured
  scope: '6a636493-8924-4402-926b-381cc9683622/.default',
  partialSecret: 'bHR8Q~yJ6SQsxQd2W4AsKQbCuqvv.d27be9Y5' // Missing last 3 characters for security
};

export const setCredentials = (clientId, clientSecret, scope) => {
  credentials = { ...credentials, clientId, clientSecret, scope };
};

export const getCredentials = () => credentials;

// Request property data via proxy server
export const searchPropertyByAPN = async (searchParams) => {
  if (!credentials.clientId || !credentials.clientSecret) {
    throw new Error('DataTrace credentials not configured. Please set credentials first.');
  }

  console.log('Searching DataTrace for:', searchParams);

  let response;
  try {
    response = await fetch('/api/datatrace/search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        credentials: {
          clientId: credentials.clientId,
          clientSecret: credentials.clientSecret,
          scope: credentials.scope || `${credentials.clientId}/.default`
        },
        searchParams
      }),
    });
  } catch (fetchError) {
    const errorDetails = [
      `Network Error: ${fetchError.message}`,
      '',
      'Make sure the proxy server is running (npm run server)',
      '',
      '--- Search Parameters ---',
      JSON.stringify(searchParams, null, 2)
    ].join('\n');
    throw new Error(errorDetails);
  }

  const responseText = await response.text();
  console.log('Proxy Response:', responseText);

  let data;
  try {
    data = JSON.parse(responseText);
  } catch (parseError) {
    const errorDetails = [
      `JSON Parse Error: ${parseError.message}`,
      '',
      '--- Raw Response ---',
      responseText
    ].join('\n');
    throw new Error(errorDetails);
  }

  // Check for error response from proxy
  if (!response.ok || data.error) {
    const errorDetails = [
      `Error: ${data.error || `HTTP ${response.status}`}`,
      '',
      data.httpStatus ? `--- HTTP Status ---\n${data.httpStatus} ${data.httpStatusText || ''}` : '',
      '',
      '--- Details ---',
      data.details || '(no details)',
      '',
      data.rawResponse ? '--- Raw Response ---\n' + data.rawResponse : '',
      '',
      data.parseError ? '--- Parse Error ---\n' + data.parseError : '',
      '',
      data.errors ? '--- API Errors ---\n' + JSON.stringify(data.errors, null, 2) : '',
      '',
      data.fullResponse ? '--- Full Response ---\n' + JSON.stringify(data.fullResponse, null, 2) : '',
      '',
      '--- Request Sent ---',
      JSON.stringify(data.request || searchParams, null, 2)
    ].filter(Boolean).join('\n');
    throw new Error(errorDetails);
  }

  // Transform the response and include raw data for transaction logging
  const transformed = transformResponse(data.data, searchParams);

  // Add raw API response and request for transaction logging
  return {
    ...transformed,
    _rawApiResponse: data.data,  // Raw DataTrace API response
    _requestBody: data.request    // Request that was sent to DataTrace
  };
};

// Transform DataTrace API response to our app format
const transformResponse = (apiResponse, searchParams) => {
  const data = apiResponse || {}; // apiResponse is already the inner data object
  const subjectProperty = data.subject_property || {};
  const situsAddress = subjectProperty.situs_address || {};
  const parsedAddress = subjectProperty.parsed_street_address || {};
  const courtHouseRecords = data.court_house_records || [];

  // Extract vesting documents (ownership/deed info)
  const vestingDocs = [];
  const legalDescDocs = [];
  courtHouseRecords.forEach(record => {
    if (record.vesting_documents) {
      vestingDocs.push(...record.vesting_documents);
    }
    if (record.legal_description_documents) {
      legalDescDocs.push(...record.legal_description_documents);
    }
  });

  // Build street address from parsed components
  const streetAddress = [
    parsedAddress.standardized_house_number,
    parsedAddress.direction_prefix,
    parsedAddress.street_name,
    parsedAddress.street_suffix,
    parsedAddress.direction_suffix
  ].filter(Boolean).join(' ') || situsAddress.street_address || 'N/A';

  // Build ownership history from vesting documents
  const ownershipHistory = vestingDocs.map(doc => ({
    date: doc.recording_date || doc.dated_date || 'N/A',
    owner: doc.grantee || 'N/A',
    transactionType: doc.doc_type || 'Deed',
    price: 'See Document',
    documentNumber: doc.cfn || doc.cmt_id || 'N/A',
    grantor: doc.grantor || 'N/A'
  }));

  // Build documents list
  const allDocuments = [...vestingDocs, ...legalDescDocs].map(doc => ({
    recordingDate: doc.recording_date || doc.dated_date || 'N/A',
    documentType: doc.doc_type || 'Document',
    documentNumber: doc.cfn || doc.cmt_id || 'N/A',
    grantor: doc.grantor || 'N/A',
    grantee: doc.grantee || 'N/A'
  }));

  // Extract location information for FIPS and municipality
  const locationInfo = data.location_information || {};
  const assessorInfo = data.assessor_information || {};

  console.log('=== DataTrace Transform Debug ===');
  console.log('Top-level municipality:', data.municipality);
  console.log('location_information:', locationInfo);
  console.log('municipality_township:', locationInfo.municipality_township);
  console.log('county_fips:', locationInfo.county_fips);
  console.log('=== APN/Parcel ID Debug ===');
  console.log('Top-level parcel_id:', data.parcel_id);
  console.log('situsAddress.parcel_id:', situsAddress.parcel_id);
  console.log('assessorInfo.assessor_parcel_number:', assessorInfo.assessor_parcel_number);
  console.log('searchParams.apn:', searchParams.apn);

  // Try multiple sources for municipality (top-level field or nested in location_information)
  const municipality = data.municipality || locationInfo.municipality_township || '';
  console.log('Final municipality value:', municipality);

  // Try multiple sources for APN/parcel ID
  // Priority: top-level parcel_id > situs_address.parcel_id > assessor_parcel_number > searchParams
  const apn = data.parcel_id || situsAddress.parcel_id || assessorInfo.assessor_parcel_number || searchParams.apn || '';
  console.log('Final APN value:', apn);

  return {
    // Raw API response for reference
    rawResponse: apiResponse,

    property: {
      address: streetAddress,
      cityStateZip: `${situsAddress.city || ''}, ${situsAddress.state || searchParams.state || ''} ${situsAddress.zip || ''}`.trim(),
      county: situsAddress.county || searchParams.county || '',
      countyFips: locationInfo.county_fips || '',
      municipality: municipality,
      apn: apn,
      legalDescription: data.legal_description || 'Not available from DataTrace',
      propertyType: 'See DataTrace Report',
      landUse: 'See DataTrace Report',
      zoning: 'See DataTrace Report',
      lotSize: 'See DataTrace Report',
      yearBuilt: 'See DataTrace Report',
      livingArea: 'See DataTrace Report',
      bedsBaths: 'See DataTrace Report',
      propertyId: data.property_id || subjectProperty.property_id || 'N/A'
    },

    ownership: {
      currentOwner: {
        name: data.owner || (vestingDocs[0]?.grantee) || 'Not available',
        vesting: 'See recorded documents',
        mailingAddress: 'See DataTrace Report',
        acquisitionDate: vestingDocs[0]?.recording_date || 'N/A',
        acquisitionPrice: 'See Document',
        documentNumber: vestingDocs[0]?.cfn || vestingDocs[0]?.cmt_id || 'N/A'
      },
      history: ownershipHistory
    },

    tax: {
      taxYear: 'N/A - Use TaxSource product',
      totalTax: 'N/A',
      status: 'N/A',
      firstInstallment: 'N/A',
      secondInstallment: 'N/A',
      taxRateArea: 'N/A',
      assessedLand: 'N/A',
      assessedImprovement: 'N/A',
      totalAssessed: 'N/A',
      marketValue: 'N/A',
      exemptions: [],
      note: 'Tax information requires separate TaxSource product request'
    },

    documents: allDocuments,

    liens: [],

    // DataTrace specific metadata
    metadata: {
      transactionId: apiResponse.transaction_id,
      confirmationNumber: apiResponse.confirmation_number,
      productName: apiResponse.product_name,
      reportStatus: apiResponse.report_status,
      orderItemId: apiResponse.order_item_id
    }
  };
};

// Check if credentials are configured
export const isConfigured = () => {
  return !!(credentials.clientId && credentials.clientSecret);
};

export default {
  setCredentials,
  getCredentials,
  searchPropertyByAPN,
  isConfigured
};
